import { useCallback, useEffect, useRef, useState } from 'react'
import type { z } from 'zod'
import { schemaFor } from '@cache/persist.ts'
import {
	bumpGeneration,
	fetchOnce,
	holdKey,
	isHeld,
	readCache,
	tokenFor,
	writeCache,
} from '@cache/resources.ts'

// Hand-rolled fetch hook. Refetches on mount, on key change, and on window
// focus. `set` is exposed for optimistic updates — call it with the next
// value (or an updater) before firing the mutation, then `refetch` after to
// reconcile with the server.
//
// With `pollIntervalMs` it also refetches in the background while the tab is
// visible, so two family members editing at once see each other's changes
// within an interval without a manual reload.
//
// `key` is serialised to JSON so the fetcher only re-fires when the key
// actually changes by value, and doubles as the key into the shared cache in
// @cache/resources.ts. A mount that hits the cache starts with data on screen
// and refreshes underneath it. Whether the key also persists across a page
// load is declared once in @cache/persist.ts, not per call site.

export type Resource<T> = {
	data: T | undefined
	loading: boolean
	error: Error | undefined
	refetch: () => Promise<void>
	// Updater may return undefined to no-op when data hasn't loaded yet —
	// callers don't have to fabricate a value out of thin air.
	set: (next: Next<T>) => void
	// `set`, then `write`, with fetches for the key held off until the write
	// settles and a refetch after. Without the hold, a poll that starts
	// mid-write can briefly put back what the optimistic update removed.
	mutate: (next: Next<T>, write: () => Promise<unknown>) => Promise<void>
}

type Next<T> = T | undefined | ((prev: T | undefined) => T | undefined)

type Options = {
	refetchOnFocus?: boolean
	// When set, refetch in the background at this interval while the tab is
	// visible. Background refetches don't flip `loading` and swallow errors —
	// they're best-effort sync, not a user-visible load.
	pollIntervalMs?: number | undefined
}

type State<T> = {
	data: T | undefined
	loading: boolean
	error: Error | undefined
}

const seed = <T>(keyStr: string, schema: z.ZodType | undefined): State<T> => {
	const cached = readCache(keyStr, schema) as T | undefined
	return { data: cached, loading: cached === undefined, error: undefined }
}

export const useResource = <T>(
	key: ReadonlyArray<unknown>,
	fetcher: () => Promise<T>,
	options: Options = {},
): Resource<T> => {
	const { refetchOnFocus = true, pollIntervalMs } = options

	const keyStr = JSON.stringify(key)
	const schema = schemaFor(key)
	const persist = schema !== undefined

	const [state, setState] = useState<State<T>>(() => seed<T>(keyStr, schema))

	// Capture the latest fetcher in a ref so refetch's identity only depends
	// on the serialised key — consumers can pass an inline arrow without
	// causing a refetch loop.
	const fetcherRef = useRef(fetcher)
	fetcherRef.current = fetcher

	// What `set`'s updater form reads. A ref rather than a functional
	// setState, so the write-through to the cache stays out of the updater.
	const dataRef = useRef<T | undefined>(state.data)
	dataRef.current = state.data

	const runFetch = useCallback(
		async (background: boolean) => {
			// A write is in progress; `mutate` refetches once it settles.
			if (isHeld(keyStr)) return
			// A cache hit never shows a spinner: Activities and Recipes pass
			// `loading` straight down to a skeleton, which would flash over
			// data already on screen.
			if (!background) {
				setState((s) =>
					s.data === undefined && !s.loading
						? { ...s, loading: true }
						: s,
				)
			}
			const token = tokenFor(keyStr)
			try {
				const result = (await fetchOnce(
					keyStr,
					fetcherRef.current,
				)) as T
				if (tokenFor(keyStr) === token) {
					writeCache(keyStr, result, persist)
					setState({
						data: result,
						loading: false,
						error: undefined,
					})
				}
			} catch (e) {
				// A refresh that fails over data we already hold stays silent.
				// What's on screen is still the best answer we have.
				if (!background) {
					const err = e instanceof Error ? e : new Error(String(e))
					setState((s) =>
						s.data === undefined ? { ...s, error: err } : s,
					)
				}
			} finally {
				if (!background) {
					setState((s) => (s.loading ? { ...s, loading: false } : s))
				}
			}
			// keyStr is the only meaningful dep: a different key means a fresh
			// fetch, and `persist` is derived from it.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[keyStr],
	)

	const refetch = useCallback(() => runFetch(false), [runFetch])

	useEffect(() => {
		void refetch()
	}, [refetch])

	useEffect(() => {
		if (!refetchOnFocus) return
		const onFocus = () => {
			void refetch()
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [refetch, refetchOnFocus])

	useEffect(() => {
		if (pollIntervalMs === undefined) return

		let intervalId: number | undefined
		const start = () => {
			if (intervalId === undefined) {
				intervalId = window.setInterval(() => {
					void runFetch(true)
				}, pollIntervalMs)
			}
		}
		const stop = () => {
			if (intervalId !== undefined) {
				window.clearInterval(intervalId)
				intervalId = undefined
			}
		}

		// Poll only while visible; catch up immediately on return so a change
		// made while the tab was backgrounded shows up at once.
		const onVisibility = () => {
			if (document.hidden) {
				stop()
			} else {
				void runFetch(true)
				start()
			}
		}
		document.addEventListener('visibilitychange', onVisibility)
		if (!document.hidden) start()

		return () => {
			document.removeEventListener('visibilitychange', onVisibility)
			stop()
		}
	}, [runFetch, pollIntervalMs])

	// Write through to the cache as well as to state: without it, navigating
	// away straight after a mutation and back would show the pre-mutation
	// value until the refetch landed.
	const set = useCallback<Resource<T>['set']>(
		(next) => {
			const value =
				typeof next === 'function'
					? (next as (p: T | undefined) => T | undefined)(
							dataRef.current,
						)
					: next
			dataRef.current = value
			bumpGeneration(keyStr)
			writeCache(keyStr, value, persist)
			setState((s) => ({ ...s, data: value }))
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[keyStr],
	)

	const mutate = useCallback<Resource<T>['mutate']>(
		async (next, write) => {
			const release = holdKey(keyStr)
			set(next)
			try {
				await write()
			} finally {
				release()
				void refetch()
			}
		},
		[keyStr, set, refetch],
	)

	return { ...state, refetch, set, mutate }
}
